import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, LayerGroup, LayersControl, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * The client portal's parcel map (plan Task 8).
 *
 * TRUE COUNTY POLYGONS, NOT PINS. The geometry was fetched once from the county assessor
 * services by `fetch-portal-parcels.ts` and stored on the portal row; every feature already
 * carries the roster attributes it needs for its popup, so this component does no joins and
 * no lookups - it draws what it is given.
 *
 * WHAT IS DELIBERATELY ABSENT: there is no wells/springs/access layer. Those coordinates are
 * not confirmed anywhere in the source material, and a dot on a map is read as a surveyed
 * fact. Nothing here is inferred.
 *
 * Colours come from `deal.parcelGroups[group].mapColor`, so the map and the parcel table
 * agree by construction rather than by two hardcoded hexes drifting apart.
 *
 * V2 (design §2): the legend moved INSIDE the map and is the single owner of group
 * visibility; labels are permanent tooltips gated by a CSS class that a zoom listener stamps
 * on the container, so zooming never re-binds leaflet DOM. Every selection-sync prop is
 * OPTIONAL - mounted with just `{ geojson, groups }` this component still behaves as v1 did.
 */

interface ParcelFeatureProps {
  apn?: string;
  county?: string;
  acres?: number;
  owner?: string;
  group?: string;
  use?: string;
  situs?: string;
  /* A PIPELINE portal (schemaVersion 1.2, Vin's) plots deals as POINTS beside any parcel polygons.
     These are the point's facts, written by build-portal-pins.ts; a parcel feature carries none. */
  dealId?: string;
  title?: string;
  address?: string;
  statusLabel?: string | null;
  ask?: string | null;
  /** Schema 1.4: the tour sheet's letter, printed inside the pin. */
  mapLetter?: string | null;
}

/** A group opts OUT of clustering; anything unstated clusters, which is the old behaviour. */
export const groupClusters = (group: PortalMapGroup | undefined): boolean => group?.cluster !== false;

/** The key a feature is registered and selected by: a parcel's APN, or a pipeline deal's id. */
export const featureKey = (props: ParcelFeatureProps | null | undefined): string | undefined =>
  props?.apn ?? props?.dealId;

/** Point geometry = a pin (a circle marker); anything else draws as a polygon. */
export const isPointGeometry = (geometry: unknown): boolean =>
  !!geometry && typeof geometry === 'object' && (geometry as { type?: string }).type === 'Point';

interface GeoJsonFeature {
  type?: string;
  properties?: ParcelFeatureProps | null;
  geometry?: unknown;
}

export interface ParcelFeatureCollection {
  type?: string;
  features?: GeoJsonFeature[];
}

export interface PortalMapGroup {
  label?: string;
  description?: string;
  mapColor?: string;
  /*
    Whether this group's pins may be swallowed by a cluster. Default true; the contract sets it
    false for the groups that must never hide.

    Why it exists: photographed at the default zoom on 2026-09-10, "Offers in play (3)" was in the
    legend and there was no red anywhere on the map - all three live offers sat inside navy cluster
    bubbles, which are counts and carry no colour. Clustering is what makes eighty-eight pins
    readable and it is also what makes the three that matter disappear; a group-level opt-out is the
    only thing that can be both.
  */
  cluster?: boolean;
}

const FALLBACK_COLOR = '#6b6b6b';
const DEFAULT_CENTER: [number, number] = [34.05, -117.4]; // Inland Empire, used only if the
// FeatureCollection yields no valid bounds - a map with no view at all throws in leaflet.
const DEFAULT_ZOOM = 9;

/** Polygon styling for one group. fillOpacity stays low so imagery reads through. */
export const parcelStyle = (color?: string) => ({
  color: color || FALLBACK_COLOR,
  weight: 2,
  fillColor: color || FALLBACK_COLOR,
  fillOpacity: 0.35,
});

/*
  A PIN is styled differently from a polygon, and on purpose (VP8.2). A polygon is read against the
  ground it covers, so it borrows the group colour for its stroke; a pin is a dot on top of whatever
  tile happens to be underneath - a road, a roof, a parking lot - so it carries its own white ring.
  The ring is what makes a 6-colour legend readable at pin size, and it is why the palette can be
  measured against WHITE rather than against every possible tile.
*/
export const PIN_RADIUS = { base: 10, selected: 13 } as const;
export const pinStyle = (color?: string) => ({
  radius: PIN_RADIUS.base,
  color: '#ffffff',
  weight: 3,
  opacity: 1,
  fillColor: color || FALLBACK_COLOR,
  fillOpacity: 1,
});
/**
 * The one permanent label a feature carries (Leaflet allows ONE tooltip per layer, so a second bindTooltip silently
 * replaces the first - which is how the first cut of the 1.4 letter vanished behind the name label).
 *   - a pin with a tour-sheet letter (1.4, Saier's page): the LETTER, centred inside the pin and always shown, so the
 *     map reads like the client's printed tour sheet; the building's name is in its popup;
 *   - any other pin: its name, above it, from mid zoom (ninety names at fit-bounds zoom are one unreadable cloud);
 *   - a parcel: its APN and acres, centred, by size.
 */
export const featureLabel = (
  props: ParcelFeatureProps | null | undefined,
  isPin: boolean
): { content: string; options: L.TooltipOptions } => {
  const letter = props?.mapLetter;
  if (isPin && typeof letter === 'string' && /^[A-Z]$/.test(letter)) {
    return { content: letter, options: { permanent: true, direction: 'center', className: 'portal-pin-letter', opacity: 1 } };
  }
  const acres =
    typeof props?.acres === 'number' ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(props.acres) : null;
  return {
    content: isPin ? `<b>${esc(props?.title ?? '')}</b>` : `<b>${esc(props?.apn ?? '')}</b>${acres ? `<br>${acres} ac` : ''}`,
    options: {
      permanent: true,
      direction: isPin ? 'top' : 'center',
      className: `portal-lbl portal-lbl-${isPin ? 'mid' : labelTier(props?.acres)}`,
    },
  };
};
export const selectedPinStyle = (color?: string) => ({
  ...pinStyle(color),
  radius: PIN_RADIUS.selected,
  weight: 4,
});

/*
  Which basemap leads, decided by WHAT THE MAP DRAWS rather than by a constant.

  Imagery was the right lead for the specimen this component was written for - Riley's raw land, where
  a client reads the photo and a street map shows nothing. It is the wrong lead for a pipeline of
  eighty-nine buildings across Southern California: dark pins on a dark satellite blob, which is the
  defect Joseph named on 2026-09-10 ("the map easier to look at"). The two specimens want opposite
  defaults, so the rule reads the geometry: mostly points means a pipeline map and streets lead;
  anything else keeps imagery. Both layers stay available either way.
*/
export const defaultBaseLayer = (data: ParcelFeatureCollection | null | undefined): 'streets' | 'satellite' => {
  const features = data?.features ?? [];
  const points = features.filter((f) => isPointGeometry(f?.geometry)).length;
  return points > features.length - points ? 'streets' : 'satellite';
};

export const LABEL_ZOOM = { mid: 14, close: 16 } as const;

/** Which zoom tier a parcel's permanent label appears at. Big parcels label at fit-bounds
 * zoom; slivers wait for close zoom so 13 labels do not collide into noise. */
export const labelTier = (acres?: number): 'always' | 'mid' | 'close' => {
  if (typeof acres === 'number' && acres >= 20) return 'always';
  if (typeof acres === 'number' && acres >= 1) return 'mid';
  return 'close';
};

export const zoomClasses = (zoom: number): string[] => [
  ...(zoom >= LABEL_ZOOM.mid ? ['portal-zoom-mid'] : []),
  ...(zoom >= LABEL_ZOOM.close ? ['portal-zoom-close'] : []),
];

/** Legend rows - same bucketing as splitByGroup so the legend and layers agree. */
export const legendModel = (
  data: ParcelFeatureCollection | null | undefined,
  groups?: Record<string, PortalMapGroup>
): Array<{ key: string; label: string; color: string; count: number }> => {
  const features = data?.features ?? [];
  const keys = Object.keys(groups ?? {});
  // A legend counts THINGS, not features: a pipeline deal drawn as a pin AND its assessor parcel is
  // one property (measured 2026-09-09: "Offers in play (5)" over three deals). A parcel-only roster
  // keys by APN and counts exactly as before.
  const thingKey = (f: GeoJsonFeature): string =>
    f?.properties?.dealId ?? f?.properties?.apn ?? JSON.stringify(f?.geometry ?? null);
  const distinct = (fs: GeoJsonFeature[]): number => new Set(fs.map(thingKey)).size;
  const rows = keys
    .map((key) => ({
      key,
      label: groups?.[key]?.label || key,
      color: groups?.[key]?.mapColor || FALLBACK_COLOR,
      count: distinct(features.filter((f) => f?.properties?.group === key)),
    }))
    .filter((r) => r.count > 0);
  const orphans = distinct(features.filter((f) => !f?.properties?.group || !keys.includes(f.properties.group)));
  if (orphans) rows.push({ key: '', label: 'Other parcels', color: FALLBACK_COLOR, count: orphans });
  return rows;
};

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Popup markup for one parcel.
 *
 * Built as a string because leaflet's `bindPopup` owns the popup DOM - hence the explicit
 * escaping: owner names come from county data and are not trusted markup.
 */
/** The popup's kind-dependent words. Absent, it reads as a buyer's page always has. */
export interface PopupWords {
  goTo?: string;
  ask?: string;
}

export const parcelPopupHtml = (
  props: ParcelFeatureProps | null | undefined,
  withTableLink = false,
  withDealLink = false,
  withAskLink = false,
  /* The two words that follow the page's kind: a tenant's page is of buildings, and its ask is a rent. */
  words?: PopupWords
): string => {
  const p = props ?? {};
  const rows: Array<[string, string]> = [];
  // A pipeline pin leads with the deal's facts; a parcel with the county's. Both print only what
  // the feature carries - a row for a missing value is a claim the map has no basis for.
  if (p.address) rows.push(['Address', String(p.address)]);
  if (p.statusLabel) rows.push(['Status', String(p.statusLabel)]);
  if (p.ask) rows.push([words?.ask ?? 'Ask', String(p.ask)]);
  if (typeof p.acres === 'number' && Number.isFinite(p.acres)) {
    rows.push(['Acres', new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(p.acres)]);
  }
  if (p.owner) rows.push(['Owner', String(p.owner)]);
  if (p.use) rows.push(['Use', String(p.use)]);
  if (p.situs) rows.push(['Situs', String(p.situs)]);
  if (p.county) rows.push(['County', String(p.county)]);
  if (p.apn && p.title) rows.push(['APN', String(p.apn)]);

  const body = rows
    .map(([k, v]) => `<dt style="color:#6b6b6b">${esc(k)}</dt><dd style="margin:0 0 4px">${esc(v)}</dd>`)
    .join('');

  return (
    `<p class="portal-popup-apn">${esc(p.title || p.apn || 'Parcel')}</p><dl>${body}</dl>` +
    (withTableLink
      ? '<button type="button" class="portal-popup-table">View in table</button>'
      : '') +
    (withDealLink
      ? `<button type="button" class="portal-popup-deal">${esc(words?.goTo ?? 'Go to deal')}</button>`
      : '') +
    (withAskLink
      ? '<button type="button" class="portal-popup-ask">Ask Apex</button>'
      : '')
  );
};

/**
 * Whether a feature's popup offers "Go to deal".
 *
 * THE TEST IS `dealId`, NOT THE GEOMETRY. The first cut asked "is this a point?", reasoning that a
 * pipeline deal draws as a pin - and the running page disagreed within a minute: clicking Grocery
 * Outlet Cathedral City opened a popup with the deal's name, address, status and ask, and NO button,
 * because what had been clicked was the deal's assessor PARCEL. Measured on the live geojson: 88
 * points and 23 polygons, and ALL 23 polygons carry a dealId. A deal drawn twice is one deal, and
 * both drawings are a door to it.
 */
export const showsDealLink = (
  props: ParcelFeatureProps | null | undefined,
  hasHandler: boolean
): boolean => hasHandler && Boolean(props?.dealId);

/**
 * Points and everything else, kept apart.
 *
 * Only points may be clustered - leaflet.markercluster puts any layer with no `getLatLng` into its
 * non-point group, so a polygon inside a cluster group still draws but travels a path nothing here
 * tests. Splitting first keeps the polygons exactly where they were and lets ONE cluster group own
 * every pin, which is the point: six separate cluster groups would stack six bubbles over Los
 * Angeles, one per legend colour, and be worse than no clustering at all.
 */
export const splitByKind = (
  collection: ParcelFeatureCollection | null | undefined
): { points: ParcelFeatureCollection; polygons: ParcelFeatureCollection } => {
  const features = collection?.features ?? [];
  const points = features.filter((f) => isPointGeometry(f?.geometry));
  const polygons = features.filter((f) => !isPointGeometry(f?.geometry));
  return {
    points: { type: 'FeatureCollection', features: points },
    polygons: { type: 'FeatureCollection', features: polygons },
  };
};

/** Split one FeatureCollection into one collection per parcel group, in group-key order. */
export const splitByGroup = (
  data: ParcelFeatureCollection | null | undefined,
  groupKeys: string[]
): Array<{ key: string; collection: ParcelFeatureCollection }> => {
  const features = data?.features ?? [];
  const known = groupKeys
    .map((key) => ({
      key,
      collection: {
        type: 'FeatureCollection',
        features: features.filter((f) => f?.properties?.group === key),
      } as ParcelFeatureCollection,
    }))
    .filter((g) => (g.collection.features ?? []).length > 0);

  // Features whose group is missing or unrecognised still have to be visible - dropping a
  // parcel off the map because its group string changed would be a silent data loss.
  const orphans = features.filter((f) => !f?.properties?.group || !groupKeys.includes(f.properties.group));
  if (orphans.length) {
    known.push({ key: '', collection: { type: 'FeatureCollection', features: orphans } });
  }
  return known;
};

/** Bounds of every feature, or null when the geometry cannot produce a valid box. */
export const boundsOf = (data: ParcelFeatureCollection | null | undefined): L.LatLngBounds | null => {
  try {
    if (!data?.features?.length) return null;
    const b = L.geoJSON(data as never).getBounds();
    return b.isValid() ? b : null;
  } catch {
    return null;
  }
};

/**
 * "Reset view" as a real leaflet control rather than an absolutely-positioned div: leaflet
 * already owns the four corner containers, and a second element with the same corner classes
 * would sit on top of the layers control.
 */
function ResetViewControl({ bounds }: { bounds: L.LatLngBounds | null }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    const control = new L.Control({ position: 'topleft' });
    control.onAdd = () => {
      const btn = L.DomUtil.create('button', 'portal-map-reset');
      btn.setAttribute('type', 'button');
      btn.textContent = 'Reset view';
      L.DomEvent.disableClickPropagation(btn);
      L.DomEvent.on(btn, 'click', () => map.fitBounds(bounds, { padding: [20, 20] }));
      return btn;
    };
    control.addTo(map);
    return () => {
      control.remove();
    };
  }, [map, bounds]);
  return null;
}

/**
 * A pane for the pins that never cluster, above leaflet's marker pane.
 *
 * Cluster bubbles are MARKERS (pane z-index 600) and vector pins live in the overlay pane (400), so
 * an unclustered pin at the same spot as a cluster is painted over by it - which would leave the
 * live offers exactly as hidden as clustering left them.
 */
export const ALWAYS_PANE = 'portal-always';
const ALWAYS_PANE_Z = 620;
function AlwaysPane() {
  const map = useMap();
  if (!map.getPane(ALWAYS_PANE)) {
    const pane = map.createPane(ALWAYS_PANE);
    pane.style.zIndex = String(ALWAYS_PANE_Z);
  }
  return null;
}

/** Stamps portal-zoom-* classes on the map container so tooltip CSS can zoom-gate labels. */
function ZoomClassController() {
  const map = useMap();
  useEffect(() => {
    const apply = () => {
      const el = map.getContainer();
      el.classList.remove('portal-zoom-mid', 'portal-zoom-close');
      zoomClasses(map.getZoom()).forEach((c) => el.classList.add(c));
    };
    apply();
    map.on('zoomend', apply);
    return () => {
      map.off('zoomend', apply);
    };
  }, [map]);
  return null;
}

/** Re-measures leaflet when fullscreen toggles and owns scroll-wheel zoom for that mode. */
function FullscreenController({ isFull }: { isFull: boolean }) {
  const map = useMap();
  useEffect(() => {
    const t = window.setTimeout(() => map.invalidateSize(), 80);
    if (isFull) map.scrollWheelZoom.enable();
    else map.scrollWheelZoom.disable();
    return () => window.clearTimeout(t);
  }, [map, isFull]);
  return null;
}

/** The selected polygon's style - heavier stroke and fill than hover, so it reads as a state. */
const selectedStyle = (color?: string) => ({ ...parcelStyle(color), weight: 4, fillOpacity: 0.55 });

/*
  Clustering (VP8.2). Vin's map has a dense blob over Los Angeles and the Inland Empire and a long
  tail out to Barstow and the Coachella Valley; at fit-bounds zoom the blob is one unreadable pile.

  Past DISABLE_CLUSTERING_AT_ZOOM every pin draws on its own, which is what makes "zoom in to see
  them" true rather than a hope - and it is what FocusController zooms to. Spiderfy is off: it is
  built around marker icons, and these pins are vector circles.
*/
export const DISABLE_CLUSTERING_AT_ZOOM = 14;
export const MAX_CLUSTER_RADIUS = 50;

/** The cluster bubble. Neutral Apex navy with the same white ring the pins wear - a cluster is a
 *  COUNT, not a group, so borrowing any legend colour for it would state something untrue. */
export const clusterIconHtml = (count: number): string =>
  `<span class="portal-cluster-count">${count}</span>`;

/*
  The step and its PIXEL SIZE together, and the size has to be passed to leaflet.

  Measured 2026-09-10: dropping `iconSize` so CSS could own the box shipped 12 x 12 px bubbles with
  the count invisible inside them. `L.DivIcon` DEFAULTS iconSize to [12, 12] and writes it as an
  inline width/height, which beats any class - so leaving the option out did not hand the decision to
  CSS, it handed it to leaflet's default. Absent is not "unset". The class still paints; only the box
  is leaflet's, and leaflet then does the -50% anchoring for free.
*/
export const clusterStep = (count: number): { step: 'sm' | 'md' | 'lg'; size: number } =>
  count < 10 ? { step: 'sm', size: 34 } : count < 50 ? { step: 'md', size: 42 } : { step: 'lg', size: 50 };

const portalClusterIcon = (cluster: { getChildCount: () => number }) => {
  const n = cluster.getChildCount();
  const { step, size } = clusterStep(n);
  return L.divIcon({
    html: clusterIconHtml(n),
    className: `portal-cluster portal-cluster-${step}`,
    iconSize: L.point(size, size),
  });
};

/** One registered layer: the leaflet object, its group colour, and whether it draws as a pin. */
export interface RegistryEntry {
  layer: L.Path;
  color?: string;
  isPin?: boolean;
}
type Registry = React.MutableRefObject<Map<string, RegistryEntry>>;

/** The style a layer wears, by shape and selection. A pin keeps its white halo in every state. */
export const styleFor = (entry: Pick<RegistryEntry, 'color' | 'isPin'>, selected: boolean) =>
  entry.isPin
    ? selected
      ? selectedPinStyle(entry.color)
      : pinStyle(entry.color)
    : selected
      ? selectedStyle(entry.color)
      : parcelStyle(entry.color);

/** Applies selected/base styles whenever the selection changes. */
function SelectionStyleController({ selectedApn, registry }: { selectedApn: string | null | undefined; registry: Registry }) {
  useEffect(() => {
    registry.current.forEach((entry, apn) => {
      entry.layer.setStyle(styleFor(entry, apn === selectedApn));
    });
  }, [selectedApn, registry]);
  return null;
}

/** Flies to a parcel when the table asks (seq forces re-fire on repeat clicks). */
function FocusController({
  focusRequest,
  registry,
}: {
  focusRequest: { apn: string; seq: number } | null | undefined;
  registry: Registry;
}) {
  const map = useMap();
  useEffect(() => {
    if (!focusRequest) return;
    const entry = registry.current.get(focusRequest.apn);
    if (!entry) return; // parcel without geometry: table click is a no-op on the map
    const b = (entry.layer as unknown as { getBounds?: () => L.LatLngBounds }).getBounds?.();
    if (b?.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 17 });
    else {
      // A pin (circle marker) has a point, not bounds - and while it is inside a cluster it is not
      // on the map at all, so opening its popup would do nothing. Past DISABLE_CLUSTERING_AT_ZOOM
      // every pin is drawn individually, which is the cheap, deterministic way to un-cluster one.
      const ll = (entry.layer as unknown as { getLatLng?: () => L.LatLng }).getLatLng?.();
      if (ll) map.setView(ll, Math.max(map.getZoom(), DISABLE_CLUSTERING_AT_ZOOM));
    }
    entry.layer.openPopup();
  }, [focusRequest, map, registry]);
  return null;
}

export default function PortalParcelMap({
  geojson,
  groups,
  selectedApn,
  focusRequest,
  onParcelClick,
  onViewInTable,
  onGoToDeal,
  onAskAboutDeal,
  popupWords,
}: {
  geojson: ParcelFeatureCollection | null | undefined;
  groups?: Record<string, PortalMapGroup>;
  /* Sync props are all optional: a portal with no table beside the map (and the test stub)
     mounts this component with none of them and behaves exactly as it did in v1. */
  selectedApn?: string | null;
  focusRequest?: { apn: string; seq: number } | null;
  onParcelClick?: (apn: string) => void;
  onViewInTable?: (apn: string) => void;
  /* A pipeline pin's "Go to deal" (VP8.2). Given it, a pin CLICK stops scrolling the page - it
     selects and opens the popup, and the button in that popup is what travels. Joseph's words:
     clicking a property "goes to the deal but I can't even see which deal exactly". */
  onGoToDeal?: (dealId: string) => void;
  /* VP8.5: the popup's "Ask Apex" - the deal's own note thread, one click from its pin. Given only to a
     link that may write notes; the page travels to the card and opens the form. */
  onAskAboutDeal?: (dealId: string) => void;
  popupWords?: PopupWords;
}) {
  // Layer registry: leaflet owns these objects, so selection and focus reach them by APN
  // rather than by re-rendering. Entries are overwritten when layers remount on a
  // visibility toggle, which is why the map is the writer and never the reader of order.
  const registryRef = useRef(new Map<string, RegistryEntry>());
  // The hover handlers close over their feature ONCE; without this ref, mouseout would
  // repaint the selected parcel in its base style and silently drop the selection.
  const selectedRef = useRef(selectedApn);
  useEffect(() => {
    selectedRef.current = selectedApn;
  }, [selectedApn]);

  const groupKeys = useMemo(() => Object.keys(groups ?? {}), [groups]);
  const layers = useMemo(() => splitByGroup(geojson, groupKeys), [geojson, groupKeys]);
  const bounds = useMemo(() => boundsOf(geojson), [geojson]);
  // Read ONCE, not per render: leaflet's LayersControl owns the checked base layer after mount, and
  // re-deciding it on every render would fight the reader's own choice.
  const [baseLayer] = useState(() => defaultBaseLayer(geojson));
  const legend = useMemo(() => legendModel(geojson, groups), [geojson, groups]);

  // The in-map legend is the single owner of group visibility (design §2): layers render
  // conditionally from this state; LayersControl keeps only the base layers.
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const isVisible = (key: string) => visible[key] !== false;

  // Split once per collection, then filter by the legend: the polygons keep their per-group layers
  // and the pins are gathered under one cluster group.
  const polygonLayers = useMemo(
    () => layers.map(({ key, collection }) => ({ key, collection: splitByKind(collection).polygons }))
      .filter(({ collection }) => (collection.features ?? []).length > 0),
    [layers]
  );
  const pinLayers = useMemo(
    () => layers.map(({ key, collection }) => ({ key, collection: splitByKind(collection).points }))
      .filter(({ collection }) => (collection.features ?? []).length > 0),
    [layers]
  );
  const visiblePinLayers = pinLayers.filter(({ key }) => isVisible(key));
  const clusteredPinLayers = visiblePinLayers.filter(({ key }) => groupClusters(key ? groups?.[key] : undefined));
  const alwaysPinLayers = visiblePinLayers.filter(({ key }) => !groupClusters(key ? groups?.[key] : undefined));

  /**
   * Everything one drawn feature needs: popup, label, registry entry, selection and hover. Shared by
   * the polygon layers and the clustered pin layers so the two cannot drift - a pin that lost its
   * halo on hover, or a polygon that gained a "Go to deal", would be that drift.
   */
  const bindFeature = (color?: string) => (feature: GeoJsonFeature, layer: L.Layer) => {
    const props = feature?.properties as ParcelFeatureProps;
    const isPin = isPointGeometry(feature?.geometry);
    const entry: RegistryEntry = { layer: layer as L.Path, color, isPin };
    layer.bindPopup(
      parcelPopupHtml(props, Boolean(onViewInTable) && !isPin, showsDealLink(props, Boolean(onGoToDeal)), showsDealLink(props, Boolean(onAskAboutDeal)), popupWords),
      { className: 'portal-popup' }
    );
    const label = featureLabel(props, isPin);
    layer.bindTooltip(label.content, label.options);
    const apn = featureKey(props);
    if (apn) registryRef.current.set(apn, entry);
    // A legend toggle REMOUNTS this GeoJSON, and SelectionStyleController only
    // fires when the selection itself changes - so a fresh layer has to paint
    // its own selected state or an off/on round trip silently loses it.
    if (apn && apn === selectedRef.current) entry.layer.setStyle(styleFor(entry, true));
    layer.on('click', () => {
      if (apn && onParcelClick) onParcelClick(apn);
    });
    if (apn) {
      // Popup buttons are wired on open, because the popup DOM is leaflet-owned string markup.
      layer.on('popupopen', (e: L.PopupEvent) => {
        const el = e.popup.getElement();
        if (onViewInTable) {
          el?.querySelector('.portal-popup-table')?.addEventListener('click', () => onViewInTable(apn), { once: true });
        }
        const goTo = onGoToDeal;
        const dealId = props?.dealId;
        if (goTo && dealId && showsDealLink(props, true)) {
          el?.querySelector('.portal-popup-deal')?.addEventListener('click', () => goTo(dealId), { once: true });
        }
        const ask = onAskAboutDeal;
        if (ask && dealId && showsDealLink(props, true)) {
          el?.querySelector('.portal-popup-ask')?.addEventListener('click', () => ask(dealId), { once: true });
        }
      });
    }
    layer.on('mouseover', () =>
      entry.layer.setStyle(isPin ? { weight: 5 } : { weight: 3, fillOpacity: 0.5 })
    );
    layer.on('mouseout', () => entry.layer.setStyle(styleFor(entry, apn === selectedRef.current)));
  };

  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    if (!isFull) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFull(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isFull]);

  if (!geojson?.features?.length) return null;

  return (
    <div className={`portal-map-wrap${isFull ? ' portal-map-full' : ''}`}>
      <MapContainer
        className="portal-map"
        bounds={bounds ?? undefined}
        boundsOptions={{ padding: [20, 20] }}
        center={bounds ? undefined : DEFAULT_CENTER}
        zoom={bounds ? undefined : DEFAULT_ZOOM}
        scrollWheelZoom={false}
      >
        <AlwaysPane />
        <LayersControl position="topright">
          {/* Which one leads is decided by the geometry - see defaultBaseLayer. Imagery for land,
              streets for a pipeline of buildings; the reader can switch either way. */}
          <LayersControl.BaseLayer checked={baseLayer === 'satellite'} name="Satellite">
            {/*
              HYBRID, not bare imagery (v2.1 §2). Raw imagery of rural San Bernardino and
              Riverside gives a client nothing to orient by - no road names, no town names.
              Esri publishes the labels as separate TRANSPARENT reference tiles, so the base
              layer is a LayerGroup of three: imagery, then roads, then places. Order is the
              stacking order (later children paint on top); reordering these buries the labels
              under the photo. Our parcel tooltips and popups live in leaflet's own panes and
              stay above all three, so none of this needs z-index work.

              Rejected: Google's tiles. They require the Maps JS API or the paid Map Tiles API
              plus a billable key exposed on a public page - not worth it for imagery parity
              out here. Revisit only for a marquee buyer deal.
            */}
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
                maxZoom={19}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
                maxZoom={19}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
                maxZoom={19}
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer checked={baseLayer === 'streets'} name="Streets">
            {/*
              Esri's World Street Map, not OpenStreetMap's own tiles. Two reasons, in order: it is a
              PALER ground - beige land, white roads - which is what six pin colours have to read
              against, and it is the same public ArcGIS service family as the imagery above, so the
              attribution and the tile policy stay one thing rather than two. (OSM's own tile servers
              also ask not to be used as a production basemap.)
            */}
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
              attribution="Esri"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {/* Polygons: unclustered, one layer per group, exactly as before. */}
        {polygonLayers
          .filter(({ key }) => isVisible(key))
          .map(({ key, collection }) => {
            const color = (key ? groups?.[key] : undefined)?.mapColor;
            return (
              <GeoJSON
                key={key || 'other'}
                data={collection as never}
                style={() => parcelStyle(color)}
                onEachFeature={bindFeature(color)}
              />
            );
          })}

        {/*
          Pins: ONE cluster group over every visible group, so the Los Angeles / Inland Empire blob
          collapses into one bubble instead of six. The key rebuilds the group when the visible set
          changes - markercluster does support adding and removing a LayerGroup, but a rebuild is the
          path this component's tests actually walk.
        */}
        {clusteredPinLayers.length > 0 && (
          <MarkerClusterGroup
            key={`pins-${clusteredPinLayers.map(({ key }) => key || 'other').join('|')}`}
            iconCreateFunction={portalClusterIcon}
            maxClusterRadius={MAX_CLUSTER_RADIUS}
            disableClusteringAtZoom={DISABLE_CLUSTERING_AT_ZOOM}
            spiderfyOnMaxZoom={false}
            showCoverageOnHover={false}
            chunkedLoading
          >
            {clusteredPinLayers.map(({ key, collection }) => {
              const color = (key ? groups?.[key] : undefined)?.mapColor;
              return (
                <GeoJSON
                  key={key || 'other'}
                  data={collection as never}
                  pointToLayer={(_feature, latlng) => L.circleMarker(latlng, pinStyle(color))}
                  onEachFeature={bindFeature(color)}
                />
              );
            })}
          </MarkerClusterGroup>
        )}

        {/* The groups the contract says must never hide, drawn above the bubbles. */}
        {alwaysPinLayers.map(({ key, collection }) => {
          const color = (key ? groups?.[key] : undefined)?.mapColor;
          return (
            <GeoJSON
              key={`always-${key || 'other'}`}
              data={collection as never}
              pane={ALWAYS_PANE}
              pointToLayer={(_feature, latlng) => L.circleMarker(latlng, { ...pinStyle(color), pane: ALWAYS_PANE })}
              onEachFeature={bindFeature(color)}
            />
          );
        })}

        <ResetViewControl bounds={bounds} />
        <ZoomClassController />
        <FullscreenController isFull={isFull} />
        <SelectionStyleController selectedApn={selectedApn} registry={registryRef} />
        <FocusController focusRequest={focusRequest} registry={registryRef} />
      </MapContainer>

      {/* React-owned overlays: legend (bottom-left) + fullscreen toggle (top-right). */}
      <div className="portal-map-legend-overlay" onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        {legend.map((row) => (
          <button
            type="button"
            key={row.key || 'other'}
            className={`portal-legend-row${isVisible(row.key) ? '' : ' portal-legend-off'}`}
            onClick={() => setVisible((v) => ({ ...v, [row.key]: !isVisible(row.key) }))}
          >
            <i className="portal-swatch" style={{ background: row.color }} />
            {row.label} ({row.count})
          </button>
        ))}
      </div>
      <button
        type="button"
        className="portal-map-fullbtn"
        onClick={() => setIsFull((f) => !f)}
        aria-label={isFull ? 'Exit fullscreen' : 'View map fullscreen'}
      >
        {isFull ? '✕ Close' : '⤢ Fullscreen'}
      </button>
    </div>
  );
}
